import { useMemo } from "react";

import { Badge } from "@/components/_atoms/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/_atoms/sheet";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useGetClimateAction } from "@/domain/climate-action/hooks/api/use-get-climate-action";
import { useGetIndicator } from "@/domain/indicator/hooks/api/use-get-indicator";
import { useGetProject } from "@/domain/project/hooks/api/use-get-project";

import { extractCurrentDisplayValue, extractIndicatorCurrentValue } from "../../lib/extract-current-value";
import { formatLocaleDate } from "../../lib/field-format";
import { toFieldResponseMap } from "../../lib/to-field-response-map";
import type { DataRequestEntity, DataRequestFreeTextQuestion } from "../../models/data-request.model";
import { type EntityMode } from "../create-data-request-dialog/create-data-request-state";
import { getAvailableFields } from "../create-data-request-dialog/data-request-fields";
import { DataRequestDocumentsSection } from "../data-request-documents-section/data-request-documents-section";

import { EntityResponseFieldRow } from "./entity-response-field-row";
import { useFieldValueResolver } from "./use-field-value-resolver";

interface EntityResponseSheetProps {
  entity: DataRequestEntity | null;
  fieldSelection: string[] | null | undefined;
  enableDocuments?: boolean;
  freeTextQuestions?: DataRequestFreeTextQuestion[] | null;
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EntityResponseSheet({
  entity,
  fieldSelection,
  enableDocuments,
  freeTextQuestions,
  workspaceId,
  open,
  onOpenChange,
}: EntityResponseSheetProps) {
  const { t } = useDomainTranslation("data-request");
  const { resolve } = useFieldValueResolver({ workspaceId });

  const isClimateAction = entity?.entityType === "CLIMATE_ACTION";
  const isProject = entity?.entityType === "PROJECT";
  const isIndicator = entity?.entityType === "INDICATOR";

  const { climateAction } = useGetClimateAction({ id: entity?.entityId, enabled: open && isClimateAction });
  const { project } = useGetProject({ id: entity?.entityId, enabled: open && isProject });
  const { indicator } = useGetIndicator({ id: entity?.entityId, enabled: open && isIndicator });

  const sourceEntity = isClimateAction ? climateAction : project;

  const responseMap = useMemo(() => toFieldResponseMap(entity?.fieldResponses), [entity?.fieldResponses]);

  if (!entity) return null;

  const familyFields = getAvailableFields([entity.entityType as EntityMode]);
  const visibleFieldList =
    fieldSelection === null || fieldSelection === undefined
      ? familyFields
      : familyFields.filter((field) => fieldSelection.includes(field.key));

  const rowFieldList = visibleFieldList.filter((field) => field.fieldType !== "indicator-values");
  const showValuesList = isIndicator && visibleFieldList.some((field) => field.fieldType === "indicator-values");

  const answeredCount = visibleFieldList.filter((f) => {
    const response = responseMap[f.key];
    return response && !response.notPossible;
  }).length;
  const notPossibleCount = visibleFieldList.filter((f) => responseMap[f.key]?.notPossible).length;

  const currentValueFor = (fieldKey: string): string | null => {
    if (isIndicator) {
      return indicator ? extractIndicatorCurrentValue(indicator, fieldKey) : null;
    }
    return sourceEntity ? extractCurrentDisplayValue(sourceEntity, fieldKey) : null;
  };

  const dataLineNameMap = new Map((indicator?.dataLineList ?? []).map((line) => [line.id, line.name]));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-96 flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <SheetHeader className="space-y-0 border-b px-4 py-3">
          <SheetTitle className="text-sm font-medium">{entity.entityTitle ?? entity.entityId}</SheetTitle>
          <SheetDescription className="flex items-center gap-2 text-xs">
            <Badge variant={entity.status === "COMPLETED" ? "secondary" : "outline"} className="text-xs">
              {t(`entityStatus.${entity.status}`)}
            </Badge>
            <span className="text-muted-foreground">
              {answeredCount}/{visibleFieldList.length} {t("detail.responseSheet.answered")}
            </span>
            {notPossibleCount > 0 && (
              <span className="text-warning-foreground">
                {notPossibleCount} {t("detail.responseSheet.notPossible")}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col">
            {rowFieldList.map((field, index) => (
              <EntityResponseFieldRow
                key={field.key}
                field={field}
                response={responseMap[field.key]}
                currentValue={currentValueFor(field.key)}
                showSeparator={index > 0}
                resolve={resolve}
              />
            ))}
          </div>

          {showValuesList && (
            <div className="border-t px-4 py-3">
              <p className="mb-2 text-sm font-medium">{t("create.fields.values")}</p>
              {indicator && indicator.values.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {indicator.values.map((value) => (
                    <div key={value.id} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatLocaleDate(value.date, "de-DE") ?? ""}
                        {dataLineNameMap.get(value.dataLineId) ? ` · ${dataLineNameMap.get(value.dataLineId)}` : ""}
                      </span>
                      <span className="font-medium">{value.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("dataEntry.values.empty")}</p>
              )}
            </div>
          )}

          {enableDocuments && (
            <DataRequestDocumentsSection
              ownerId={entity.id}
              isEditing={false}
              label={t("detail.documents")}
              className="px-4 pb-4"
            />
          )}

          {freeTextQuestions && freeTextQuestions.length > 0 && (
            <div className="border-t px-4 py-3">
              <p className="mb-2 text-sm font-medium">{t("detail.freeText")}</p>
              <div className="flex flex-col gap-3">
                {freeTextQuestions.map((question) => {
                  const answer = entity.freeTextAnswers?.[question.id];
                  return (
                    <div key={question.id} className="flex flex-col gap-0.5">
                      <span className="text-xs text-muted-foreground">{question.label}</span>
                      {answer ? (
                        <p className="whitespace-pre-wrap text-sm">{answer}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t("detail.freeTextEmpty")}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
